import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";

const blank = { title: "Formule", subtitle: "", price: "", lines: [""], note: "" };

function FormuleCard({ value, onChange, onRemove, label, testId }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setLine = (i, v) => set("lines", value.lines.map((l, j) => (j === i ? v : l)));
  return (
    <div className="ic-card !transform-none p-4 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="gold-label">{label}</p>
        {onRemove && <button type="button" onClick={onRemove} className="p-1 text-gray-500 hover:text-[#ff6b81]" data-testid={`${testId}-remove`}><Trash2 size={15} /></button>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input className="ic-input" placeholder="Titre (ex : Formule Midi)" value={value.title} onChange={(e) => set("title", e.target.value)} data-testid={`${testId}-title`} />
        <input className="ic-input" placeholder="Sous-titre (ex : Jusqu'à 10 ans)" value={value.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid={`${testId}-subtitle`} />
        <input className="ic-input" required placeholder="Prix (ex : 18,90 €)" value={value.price} onChange={(e) => set("price", e.target.value)} data-testid={`${testId}-price`} />
      </div>
      <div className="space-y-2">
        {value.lines.map((l, i) => (
          <div key={i} className="flex gap-2">
            <input className="ic-input flex-1" placeholder={`Ligne ${i + 1} (ex : Entrée + Plat ou Pizza)`} value={l} onChange={(e) => setLine(i, e.target.value)} data-testid={`${testId}-line-${i}`} />
            {value.lines.length > 1 && <button type="button" onClick={() => set("lines", value.lines.filter((_, j) => j !== i))} className="p-2 text-gray-500 hover:text-[#ff6b81]"><Trash2 size={14} /></button>}
          </div>
        ))}
        <button type="button" onClick={() => set("lines", [...value.lines, ""])} className="text-xs text-[#D4AF37] hover:underline" data-testid={`${testId}-add-line`}>+ Ajouter une ligne</button>
      </div>
      <textarea className="ic-input" rows={2} placeholder="Note (choix, suppléments, conditions…)" value={value.note || ""} onChange={(e) => set("note", e.target.value)} data-testid={`${testId}-note`} />
    </div>
  );
}

export function AdminFormules() {
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/formules").then((r) => setDoc(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const clean = (f) => ({ ...f, lines: f.lines.map((l) => l.trim()).filter(Boolean) });
    try {
      const r = await api.put("/admin/formules", { formules: doc.formules.map(clean), menu_enfant: doc.menu_enfant ? clean(doc.menu_enfant) : null });
      setDoc(r.data);
      toast.success("Formules enregistrées — visibles sur la carte");
    } catch { toast.error("Erreur lors de l'enregistrement"); } finally { setSaving(false); }
  };

  if (!doc) return null;
  const setF = (i, v) => setDoc({ ...doc, formules: doc.formules.map((f, j) => (j === i ? v : f)) });

  return (
    <div className="ic-card !transform-none p-5 mb-8" data-testid="admin-formules">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="formules-toggle">
        <div className="text-left">
          <h2 className="font-serif-elegant text-xl font-bold">Formules midi & Menu enfant</h2>
          <p className="text-xs text-gray-500">{doc.formules.length} formule(s){doc.menu_enfant ? " · menu enfant" : ""} — affichées en haut de la carte</p>
        </div>
        {open ? <ChevronUp size={18} className="text-[#D4AF37]" /> : <ChevronDown size={18} className="text-[#D4AF37]" />}
      </button>
      {open && (
        <form onSubmit={save} className="mt-5 space-y-4">
          {doc.formules.map((f, i) => (
            <FormuleCard key={i} value={f} label={`Formule ${i + 1}`} testId={`formule-${i}`} onChange={(v) => setF(i, v)}
              onRemove={() => setDoc({ ...doc, formules: doc.formules.filter((_, j) => j !== i) })} />
          ))}
          <button type="button" onClick={() => setDoc({ ...doc, formules: [...doc.formules, { ...blank }] })} className="btn-outline-gold !py-2 text-sm" data-testid="formules-add"><Plus size={15} /> Ajouter une formule</button>
          {doc.menu_enfant ? (
            <FormuleCard value={doc.menu_enfant} label="Menu enfant" testId="menu-enfant" onChange={(v) => setDoc({ ...doc, menu_enfant: v })} onRemove={() => setDoc({ ...doc, menu_enfant: null })} />
          ) : (
            <button type="button" onClick={() => setDoc({ ...doc, menu_enfant: { ...blank, title: "Menu Enfant" } })} className="btn-outline-gold !py-2 text-sm" data-testid="menu-enfant-add"><Plus size={15} /> Ajouter un menu enfant</button>
          )}
          <div><button type="submit" disabled={saving} className="btn-gold !py-2 text-sm disabled:opacity-40" data-testid="formules-save"><Save size={15} /> {saving ? "Enregistrement…" : "Enregistrer les formules"}</button></div>
        </form>
      )}
    </div>
  );
}
